import {memo, shapeComponent, ShapeComponent, Text, useBreakpoint} from "shared/base"
import {Pressable, View} from "react-native"
import React, {useMemo} from "react"
import {digg} from "diggerize"
import FontAwesomeIcon from "react-native-vector-icons/FontAwesome"
import I18n from "shared/i18n"
import moment from "shared/moment"
import Params from "@kaspernj/api-maker/build/params"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useDates from "components/utils/use-dates"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"

export default memo(shapeComponent(class ComponentsUserDashbarWeekBar extends ShapeComponent {
  static defaultProps = {
    dateFrom: null,
    dateTo: null,
    weekNavigation: false
  }

  static propTypes = propTypesExact({
    dateFrom: PropTypes.instanceOf(Date),
    dateTo: PropTypes.instanceOf(Date),
    weekNavigation: PropTypes.bool.isRequired
  })

  setup() {
    const {t} = useI18n({namespace: "js.components.user.dashboard.week_bar"})
    const {l} = useDates()

    this.currentUser = useCurrentUser()
    this.dateFrom = useMemo(() => this.p.dateFrom || moment().startOf("isoWeek").toDate(), [this.p.dateFrom])
    this.dateTo = useMemo(() => this.p.dateTo || moment().endOf("isoWeek").toDate(), [this.p.dateTo])
    this.t = t
    this.l = l
    this.setInstance(useBreakpoint())

    this.useStates({
      numberOfWoofsOnTheSchool: undefined,
      numberOfWoofsInYourClasses: undefined
    })

    useMemo(() => {
      if (this.currentUser) {
        this.loadNumbers()
      }
    }, [this.currentUser?.id(), this.tt.dateFrom, this.tt.dateTo])
  }

  async loadNumbers() {
    const result = await this.tt.currentUser.weekBar({
      date_from: this.tt.dateFrom,
      date_to: this.tt.dateTo
    })

    this.setState({
      numberOfWoofsOnTheSchool: digg(result, "number_of_woofs_on_the_school"),
      numberOfWoofsInYourClasses: digg(result, "number_of_woofs_in_your_classes")
    })
  }

  render() { // eslint-disable-line complexity
    const {dateFrom, dateTo, l, mdUp, t} = this.tt
    const currentDate = moment()
    const fromDate = moment(dateFrom)
    let sameWeek = false
    let dateFromText, dateToText

    if (!dateFrom || (currentDate.year() == fromDate.year() && currentDate.isoWeek() == fromDate.isoWeek())) {
      sameWeek = true
    }

    if (dateFrom) {
      dateFromText = l(dateFrom, "date")
    }

    if (dateTo) {
      dateToText = l(dateTo, "date")
    }

    return (
      <View
        dataSet={this.rootViewDataSet ||= {component: "user/dashboard/week-bar"}}
        style={this.rootViewStyle ||= {
          flexDirection: "row",
          flexShrink: 1,
          alignItems: "center",
          paddingVertical: 8,
          paddingHorizontal: 24,
          borderRadius: 30,
          backgroundColor: "#fff"
        }}
      >
        {this.p.weekNavigation &&
          <>
            <Pressable
              dataSet={this.previousWeekButtonDataSet ||= {class: "previous-week-button"}}
              onPress={this.tt.onPreviousWeekPress}
              style={this.previousWeekButtonStyle ||= {
                position: "absolute",
                top: 14,
                left: -25
              }}
            >
              <FontAwesomeIcon name="chevron-left" size={20} />
            </Pressable>
            <Pressable
              dataSet={this.nextWeekButtonDataSet ||= {class: "next-week-button"}}
              onPress={this.tt.onNextWeekPress}
              style={this.nextWeekButtonStyle ||= {
                position: "absolute",
                top: 14,
                right: -25
              }}
            >
              <FontAwesomeIcon name="chevron-right" size={20} />
            </Pressable>
          </>
        }
        <Text style={this.yourWeekTextStyle ||= {color: "#2c2c96", fontSize: 16, fontWeight: "bold"}}>
          {(() => {
            if (sameWeek) {
              return t(".your_week")
            } else if (dateFrom && dateTo) {
              return t(".week_with_number_and_dates", {week: fromDate.isoWeek(), date_from: dateFromText, date_to: dateToText})
            } else {
              return t(".week_with_number", {week: fromDate.isoWeek()})
            }
          })()}
        </Text>
        <View
          dataSet={this.pawCircleIconContainerDataSet ||= {class: "paw-circle-icon"}}
          style={this.pawCircleIconContainerStyle ||= {
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            marginLeft: 24,
            marginRight: 24,
            borderRadius: "50%",
            backgroundColor: "#5253ff"
          }}
        >
          <FontAwesomeIcon name="paw" size={14} style={this.pawCircleIconStyle ||= {color: "#fff"}} />
        </View>
        <View
          dataSet={this.numberOfWoofsOnTheSchoolContainerDataSet ||= {class: "number-of-woofs-on-the-school-container"}}
          style={this.cache("numberOfWoofsOnTheSchoolContainerStyle", {
            flexDirection: mdUp ? "row" : "column",
            alignItems: "center",
            marginRight: 24,
            marginLeft: 5
          }, [mdUp])}
        >
          {this.s.numberOfWoofsOnTheSchool !== undefined &&
            <>
              <Text style={this.numberOfWoofsOnTheSchoolCountTextStyle ||= {color: "#2c2c96", fontSize: 14, fontWeight: 700}}>
                {t(".number_of_woofs", {number: this.s.numberOfWoofsOnTheSchool})}
              </Text>
              <Text style={this.numberOfWoofsOnTheSchoolTextStyle ||= {marginLeft: 4, color: "#2c2c96", fontSize: 14}}>
                {t(".on_the_school")}
              </Text>
            </>
          }
        </View>
        <View
          dataSet={this.numberOfWoofsOnYourClassesContainerDataSet ||= {class: "number-of-woofs-in-your-classes-container"}}
          style={this.cache("numberOfWoofsOnYourClassesContainerStyle", {
            flexDirection: mdUp ? "row" : "column",
            alignItems: "center"
          }, [mdUp])}
        >
          {this.s.numberOfWoofsInYourClasses !== undefined &&
            <>
              <Text style={this.numberOfWoofsOnYourClassesCountTextStyle ||= {color: "#2c2c96", fontSize: 14, fontWeight: 700}}>
                {t(".number_of_woofs", {number: this.s.numberOfWoofsOnTheSchool})}
              </Text>
              <Text style={this.numberOfWoofsOnYourClassesTextStyle ||= {marginLeft: 4, color: "#2c2c96", fontSize: 14}}>
                {t(".in_your_classes")}
              </Text>
            </>
          }
        </View>
      </View>
    )
  }

  onNextWeekPress = () => {
    const nextWeekStart = moment(this.tt.dateFrom).add(1, "week")
    const nextWeekEnd = moment(nextWeekStart).add(1, "week").subtract(1, "day")

    Params.changeParams({
      date_from: I18n.strftime("%Y-%m-%d", nextWeekStart.toDate()),
      date_to: I18n.strftime("%Y-%m-%d", nextWeekEnd.toDate())
    })
  }

  onPreviousWeekPress = () => {
    const previousWeekStart = moment(this.tt.dateFrom).subtract(1, "week")
    const previousWeekEnd = moment(previousWeekStart).add(1, "week").subtract(1, "day")

    Params.changeParams({
      date_from: I18n.strftime("%Y-%m-%d", previousWeekStart.toDate()),
      date_to: I18n.strftime("%Y-%m-%d", previousWeekEnd.toDate())
    })
  }
}))
